/**
 * The ONE navigation registry for the /admin surface. Nav (sidebar) and
 * CommandPalette (⌘K) both render from this list so a new admin page added
 * here shows up in both automatically — never re-declare nav entries in
 * either consumer.
 *
 * Items are data-only: `Icon` is the lucide component (consumers size it),
 * `isActiveFn(segments)` reproduces each tab's historical active logic.
 */
import {
  BarChart3,
  BookPlus,
  CalendarDays,
  Camera,
  CreditCard,
  FileCode,
  FileText,
  Globe,
  LayoutGrid,
  Mail,
  MapPin,
  MessagesSquare,
  Palette,
  Pencil,
  Receipt,
  Settings,
  Sparkles,
  Users,
} from 'lucide-react';
import { getSportLabel } from '@staty/shared/utils/sportLabels';
import { publicOrgOrigin } from '@/utils/publicSiteUrl';

/** The org/league context the registry needs; `league` is read for its sport
 *  and (INT-0007) its custom domain. */
export interface NavContext {
  league?: {
    sports?: string[];
    name?: string;
    customDomain?: { hostname?: string; status?: string } | null;
  } | null;
  leagueName?: string;
}

export function getNavSections({ league, leagueName }: NavContext) {
  return [
    {
      key: 'overview',
      label: 'Overview',
      items: [
        {
          name: 'Get Started',
          href: '/admin/get-started',
          Icon: Sparkles,
          dataTour: 'nav-get-started',
          isActiveFn: (segments: string[]) => segments.includes('get-started'),
        },
        {
          name: 'Dashboard',
          href: '/admin/dashboard',
          Icon: BarChart3,
          dataTour: 'nav-dashboard',
          isActiveFn: (segments: string[]) => segments.includes('dashboard'),
        },
        {
          name: 'Public Site',
          // Phase 2 flipped 2026-08-26: statyapps.io root everywhere. INT-0007:
          // the org's ACTIVE custom domain wins when one exists, falling back
          // to the tenant subdomain. On a local admin host the link targets
          // the LOCAL public app for the active org instead (owner
          // 2026-10-12) — publicOrgOrigin owns that branch.
          href: publicOrgOrigin(league, leagueName || ''),
          newTab: true,
          Icon: Globe,
          dataTour: 'nav-view-site',
          isActiveFn: () => false,
        },
      ],
    },
    {
      key: 'leagueManagement',
      label: 'League Management',
      items: [
        {
          name: 'Competitions',
          href: '/admin/organize',
          Icon: LayoutGrid,
          dataTour: 'nav-organize',
          isActiveFn: (segments: string[]) => segments.includes('organize'),
        },
        // The legacy "Tournaments" (/admin/league/tournaments) and "Leagues"
        // (/admin/league/leagues) tabs were removed from navigation 2026-08-24
        // (Structure is canonical; the Schedule hub covers scheduling entry).
        // Their routes stay URL-reachable temporarily as a safety net; never
        // re-add nav entries — the containers get deleted in a later pass.
        {
          name: getSportLabel(league?.sports?.[0], 'venues'),
          href: '/admin/locations',
          Icon: MapPin,
          dataTour: 'nav-venues',
          isActiveFn: (segments: string[]) => segments.includes('locations'),
        },
        {
          name: 'Schedule',
          href: '/admin/scheduler',
          Icon: CalendarDays,
          dataTour: 'nav-scheduler',
          isActiveFn: (segments: string[]) => segments.includes('scheduler'),
        },
        {
          name: 'Game Results',
          href: '/admin/game-results',
          Icon: Camera,
          dataTour: 'nav-game-results',
          isActiveFn: (segments: string[]) => segments.includes('game-results'),
        },
      ],
    },
    {
      key: 'registration',
      label: 'Registration & Teams',
      items: [
        {
          name: 'Registrations',
          href: '/admin/registration',
          Icon: BookPlus,
          dataTour: 'nav-registration',
          isActiveFn: (segments: string[]) => segments.includes('registration'),
        },
        {
          name: 'Admins',
          href: '/admin/members',
          Icon: Users,
          dataTour: 'nav-members',
          isActiveFn: (segments: string[]) => segments.includes('members'),
        },
        {
          name: 'Messages',
          href: '/admin/messages',
          Icon: MessagesSquare,
          dataTour: 'nav-messages',
          isActiveFn: (segments: string[]) => segments.includes('messages'),
        },
      ],
    },
    {
      // Renamed from 'Financial' (owner 2026-09-11): the section holds the
      // two record/history surfaces — payment transactions + the activity
      // log — so the label says what's inside. Key stays 'financial'
      // (internal state id in Nav.tsx; renaming it would churn expansion
      // state for no user-visible gain).
      key: 'financial',
      label: 'Payments & Activity',
      items: [
        // 'Pricing' (/admin/payment-products) removed 2026-08-25 — payment
        // products are created/edited inline on each registration's Payments
        // pane. Route stays URL-reachable temporarily; never re-add the entry.
        {
          name: 'Payments',
          href: '/admin/transactions',
          Icon: Receipt,
          dataTour: 'nav-transactions',
          isActiveFn: (segments: string[]) => segments.includes('transactions'),
        },
        // Moved from System & Tools (owner 2026-09-11) — it's a history
        // surface like Payments, and organizers look for both together.
        {
          name: 'Activity Log',
          href: '/admin/logs',
          Icon: FileCode,
          dataTour: 'nav-logs',
          isActiveFn: (segments: string[]) => segments.includes('logs'),
        },
      ],
    },
    {
      key: 'system',
      label: 'System & Tools',
      items: [
        {
          name: 'Appearance',
          href: '/admin/appearance',
          Icon: Palette,
          dataTour: 'nav-appearance',
          isActiveFn: (segments: string[]) => segments.includes('appearance'),
        },
        {
          name: 'Site Editor',
          href: '/admin/editor',
          Icon: Pencil,
          dataTour: 'nav-layout-editor',
          isActiveFn: (segments: string[]) => segments.includes('editor'),
        },
        {
          name: 'Waivers',
          href: '/admin/waivers',
          Icon: FileText,
          dataTour: 'nav-waivers',
          isActiveFn: (segments: string[]) => segments.includes('waivers'),
        },
        {
          name: 'Email Templates',
          href: '/admin/email-templates',
          Icon: Mail,
          dataTour: 'nav-email-templates',
          isActiveFn: (segments: string[]) => segments.includes('email-templates'),
        },
        {
          name: 'Settings',
          href: '/admin/settings',
          Icon: Settings,
          dataTour: 'nav-settings',
          isActiveFn: (segments: string[]) => segments.includes('settings'),
        },
      ],
    },
  ];
}

/** Flat list for the palette's Navigate group. */
export function getFlatNavItems({ league, leagueName }: NavContext) {
  return getNavSections({ league, leagueName }).flatMap((section) =>
    section.items.map((item) => ({ ...item, section: section.label }))
  );
}

/** The custom event the ⌘K palette listens for (Nav's search button fires it). */
export const OPEN_COMMAND_PALETTE_EVENT = 'staty:open-command-palette';
